/**
 * M21 — Visão geral compacta e neutra dos equipamentos END.
 * Função pura: recebe apenas registros já autorizados e não depende do Cliente ativo.
 * Preserva a semântica atual do painel e as ações tratadas pela página.
 */
import type { NdtAsset, NdtAssetType } from "../../domain/ndt/ndt-assets.js";
import { resolveCalibrationStatus } from "../../domain/ndt/ndt-calibration.js";

export interface NdtDashboardInput {
  assets: Record<NdtAssetType, NdtAsset[]>;
  certificatesByAsset: ReadonlyMap<string, { expirationDate?: string } | null>;
  loadError: boolean;
  escapeHtml: (value: unknown) => string;
  typeLabel: (type: NdtAssetType) => string;
  requiresCertificate: (type: NdtAssetType) => boolean;
}

export function renderNdtDashboard(input: NdtDashboardInput): string {
  if (input.loadError) {
    return '<p class="ndt-overview-error" role="alert">Não foi possível carregar os equipamentos. Atualize a página para tentar novamente.</p>';
  }
  const all = Object.values(input.assets).flat();
  const active = all.filter(a => a.status === 'ACTIVE');
  const relevant = all.filter(a => input.requiresCertificate(a.assetType));
  const reference = new Date();
  const certStatus = (a: NdtAsset) => resolveCalibrationStatus(
    input.certificatesByAsset.get(a.id)?.expirationDate, reference, 30
  );
  const expired = relevant.filter(a => certStatus(a) === 'EXPIRED');
  const expiring = relevant.filter(a => certStatus(a) === 'EXPIRING');
  const noValidity = relevant.filter(a => certStatus(a) === 'NO_EXPIRATION_DEFINED');
  const available = active.filter(a =>
    !input.requiresCertificate(a.assetType) || certStatus(a) === 'VALID'
  );
  const alerts = [...expired.map(a => ({ a, text: 'Calibração vencida', kind: 'expired' })),
    ...expiring.map(a => ({ a, text: 'Calibração próxima do vencimento', kind: 'expiring' })),
    ...noValidity.map(a => ({ a, text: 'Validade não informada', kind: 'unknown' }))];
  const recent = [...all].sort((a, b) =>
    (b.createdAt || '').localeCompare(a.createdAt || '')
  ).slice(0, 5);
  const esc = input.escapeHtml;
  const card = (label: string, value: number, kind: string) =>
    `<div class="ndt-overview-card"><span>${label}</span><strong class="${kind}">${value}</strong></div>`;
  return `
    <section class="ndt-overview" aria-label="Resumo dos equipamentos END">
      <div class="ndt-overview-cards">
        ${card('Total cadastrados', all.length, '')}
        ${card('Disponíveis', available.length, 'good')}
        ${card('A vencer (30 dias)', expiring.length, 'warn')}
        ${card('Vencidos', expired.length, 'bad')}
      </div>
      <div class="ndt-overview-panel">
        <h2>Alertas de calibração</h2>
        ${alerts.length ? alerts.slice(0, 4).map(({ a, text, kind }) => `
          <button type="button" class="ndt-overview-alert" data-go-asset-type="${a.assetType}" data-dashboard-asset-id="${esc(a.id)}">
            <span class="${kind}">●</span> ${esc(a.internalCode || a.serialNumber || a.id)}
            — ${text}
          </button>`).join('') : '<p class="ndt-muted">Nenhum alerta de calibração identificado.</p>'}
      </div>
      <div class="ndt-overview-panel">
        <div class="ndt-overview-section-head"><h2>Equipamentos recentes</h2><button type="button" class="ndt-overview-open" data-ndt-all-assets>Ver equipamentos →</button></div>
        ${recent.length ? `<div class="ndt-overview-scroll"><table class="data-table">
          <thead><tr><th>TAG</th><th>Equipamento</th><th>Tipo</th><th>Situação</th></tr></thead>
          <tbody>${recent.map(a => `<tr>
            <td><button type="button" class="ndt-overview-link" data-go-asset-type="${a.assetType}" data-dashboard-asset-id="${esc(a.id)}">${esc(a.internalCode || '—')}</button></td>
            <td>${esc([a.manufacturer, a.model].filter(Boolean).join(' / ') || 'Não informado')}</td>
            <td>${esc(input.typeLabel(a.assetType))}</td>
            <td>${a.status === 'ACTIVE' ? 'Ativo' : a.status === 'UNDER_MAINTENANCE' ? 'Em manutenção' : a.status === 'INACTIVE' ? 'Inativo' : a.status === 'OUT_OF_SERVICE' ? 'Fora de serviço' : a.status === 'CALIBRATION_EXPIRED' ? 'Calibração vencida' : 'Calibração pendente'}</td>
          </tr>`).join('')}</tbody>
        </table></div>` : '<p class="ndt-muted">Nenhum equipamento cadastrado.</p>'}
      </div>
    </section>`;
}
